import type { AdditionalData, ErrorDetailsData, ErrorReport, ErrorStack } from './types';

type AxiosLikeError = {
  config?: {
    baseURL?: string;
    data?: unknown;
    method?: string;
    params?: unknown;
    url?: string;
  };
  isAxiosError: true;
  response?: {
    data?: unknown;
    headers?: unknown;
    status: number;
    statusText: string;
  };
} & Error;

const isAxiosLikeError = (error: unknown): error is AxiosLikeError =>
  error instanceof Error && Reflect.get(error, 'isAxiosError') === true;

const getErrorChain = (error: Error): Error[] =>
  error.cause instanceof Error ? [...getErrorChain(error.cause), error] : [error];

const getErrorStacks = (error: Error): ErrorStack[] =>
  getErrorChain(error).map((chainError) => ({
    name: chainError.name,
    stack: chainError.stack?.split('\n').map((line) => line.trim()) ?? [],
  }));

const getErrorReport = (error: unknown): ErrorReport => {
  const errorSummary: ErrorReport['errorSummary'] =
    error instanceof Error
      ? { message: error.message, name: error.name, stacks: getErrorStacks(error) }
      : { message: String(error), name: 'Unknown error' };

  if (!isAxiosLikeError(error)) {
    return { errorSummary };
  }

  return {
    errorSummary,
    request: {
      baseURL: error.config?.baseURL,
      data: error.config?.data,
      method: error.config?.method,
      params: error.config?.params,
      url: error.config?.url,
    },
    response: error.response
      ? {
          data: error.response.data,
          headers: error.response.headers,
          status: error.response.status,
          statusText: error.response.statusText,
        }
      : undefined,
  };
};

export const getErrorDetails = (error: unknown, additionalData?: AdditionalData[]): ErrorDetailsData => {
  const errors = error instanceof AggregateError ? (error.errors as unknown[]) : [error];

  return {
    additionalData,
    errors: errors.map((item) => getErrorReport(item)),
    location: window.location.href,
    timestamp: new Date(),
    userAgent: navigator.userAgent,
  };
};
